<template>
<div class="login-page">
  <el-dropdown class="login-language-switch" trigger="click" placement="bottom-end" data-i18n-ignore="true">
    <button type="button" class="login-language-button" :aria-label="$t('语言')">
      <i class="el-icon-sort" aria-hidden="true" />
      <span>{{ $locale === 'en' ? 'EN' : '中文' }}</span>
      <i class="el-icon-arrow-down" aria-hidden="true" />
    </button>
    <el-dropdown-menu slot="dropdown" class="language-dropdown">
      <el-dropdown-item :class="{ 'language-selected': $locale === 'zh' }" @click.native="$setLocale('zh')">中文</el-dropdown-item>
      <el-dropdown-item :class="{ 'language-selected': $locale === 'en' }" @click.native="$setLocale('en')">English</el-dropdown-item>
    </el-dropdown-menu>
  </el-dropdown>

  <div class="login-card">
    <div class="login-accent" aria-hidden="true"></div>

    <div class="login-brand-row">
      <img class="login-logo" :src="logo" alt="LabelCheap" />
    </div>

    <p class="login-welcome">{{ welcomeText }}</p>
    <p class="login-subtitle">{{ loginSubtitle }}</p>

    <el-form ref="loginForm" class="login-form" :model="loginForm" :rules="loginRules" @submit.native.prevent>
      <el-form-item prop="username">
        <label class="login-label">{{ $t('用户名') }}</label>
        <el-input v-model="loginForm.username" :placeholder="$t('请输入用户名')" @keyup.enter.native="handleLogin"></el-input>
      </el-form-item>

      <el-form-item prop="password">
        <label class="login-label">{{ $t('密码') }}</label>
        <el-input v-model="loginForm.password" type="password" show-password :placeholder="$t('请输入密码')" @keyup.enter.native="handleLogin"></el-input>
      </el-form-item>

      <el-form-item prop="code">
        <slider-captcha ref="sliderCaptcha" :disabled="loading" @change="handleCaptchaChange" @busy="captchaLoading = $event" />
      </el-form-item>

      <div class="login-forgot">
        <a href="javascript:void(0)" @click="handleForgot">{{ $t('忘记密码？') }}</a>
      </div>

      <el-button class="login-button" type="primary" :loading="loading" :disabled="!canSubmit || loading" @click="handleLogin()">
        <span v-if="loading">{{ $t('正在登录中...') }}</span>
        <span v-else>{{ $t('登录') }}</span>
      </el-button>
    </el-form>
  </div>

  <span class="footer-span">LabelCheap · <span class="footer-target">{{ loginTitle }}</span><router-link class="login-welcome-link" to="/welcome">{{ $t('了解 LabelCheap') }} ↗</router-link></span>
</div>
</template>

<script>
import { isNavigationFailure, NavigationFailureType } from 'vue-router'
import { getLoginPath } from '../../utils/loginEntry'
import { translateUi } from '../../i18n'
import SliderCaptcha from '../../components/SliderCaptcha'
import { SYSTEM } from '../../constant/index'
import logoImage from '../../assets/images/logo.png'

export default {
  name: 'Login',
  components: { SliderCaptcha },

  data () {
    return {
      // 带名称的品牌 logo
      logo: logoImage,
      // 加载等待框
      loading: false,
      // 验证码
      captchaLoading: false,
      // 用户登录表单
      loginForm: {
        // 用户名
        username: '',
        // 密码
        password: '',
        // 验证码
        code: undefined,
        // 验证码唯一值
        traceId: undefined,
        // 系统登录
        channel: SYSTEM,
        // 根据当前入口同步确定登录目标，避免客户入口首屏短暂显示管理员文案
        target: getLoginPath() === '/portal/sign-in' ? 'CUSTOMER' : 'ADMIN'
      },
      // 登录校验规则
      loginRules: {
        // 用户名校验规则
        username: [
          { required: true, get message () { return translateUi('用户名不能为空') }, trigger: 'blur' }
        ],
        // 密码校验规则
        password: [
          { required: true, get message () { return translateUi('密码不能为空') }, trigger: 'blur' }
        ],
        // 验证码校验规则
        code: [{ required: true, get message () { return translateUi('请先完成拖动验证') }, trigger: 'blur' }]
      }
    }
  },
  computed: {
    // 登录类型标题（管理后台 / 客户门户）
    loginTitle () {
      return this.loginForm.target === 'CUSTOMER' ? '客户门户登录' : '管理员后台登录'
    },
    // 欢迎语
    welcomeText () {
      return this.loginForm.target === 'CUSTOMER' ? '欢迎回来' : '欢迎使用 LabelCheap'
    },
    // 副标题
    loginSubtitle () {
      return this.loginForm.target === 'CUSTOMER' ? '客户门户 · 请输入账号继续' : '管理员后台 · 请输入账号继续'
    },
    // 表单填写完整后才允许提交
    canSubmit () {
      const form = this.loginForm
      return !!(!this.captchaLoading && form.username && form.password && form.code && form.traceId)
    }
  },
  mounted () {
    if (sessionStorage.getItem('accountDisabledNotice') === '1') {
      sessionStorage.removeItem('accountDisabledNotice')
      sessionStorage.removeItem('idleLogoutNotice')
      this.$message.error(translateUi('账号被禁用，请联系管理员'))
    } else if (sessionStorage.getItem('idleLogoutNotice') === '1') {
      sessionStorage.removeItem('idleLogoutNotice')
      this.$message.warning(translateUi('长时间未操作，已自动退出，请重新登录'))
    }
  },
  methods: {
    /**
     * 获取验证码
     */
    async getCaptcha () {
      if (this.loading) return
      await this.$nextTick()
      if (this.$refs.sliderCaptcha) await this.$refs.sliderCaptcha.reset()
    },
    handleCaptchaChange (proof) {
      this.loginForm.code = proof ? proof.code : ''
      this.loginForm.traceId = proof ? proof.traceId : undefined
      if (this.$refs.loginForm) this.$refs.loginForm.clearValidate('code')
    },
    /**
     * 忘记密码提示
     */
    handleForgot () {
      this.$message.info(translateUi('请联系系统管理员重置密码'))
    },
    /**
     * 用户登录
     */
    handleLogin () {
      if (this.loading || this.captchaLoading) return
      if (!this.loginForm.code || !this.loginForm.traceId) return this.$message.warning(translateUi('请先完成拖动验证'))
      return this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          if (this.loading || this.captchaLoading) return
          if (!this.loginForm.traceId) return this.getCaptcha()
          this.loading = true
          try {
            await this.$store.dispatch('user/login', this.loginForm)
            sessionStorage.setItem('loginTarget', this.loginForm.target)
            this.$message.success(translateUi('登录成功'))
            // 管理员和客户统一进入首页，页面按角色展示对应工作台
            const navigation = await this.$router.replace('/home')
            // The router wrapper returns errors. Route generation redirects are expected.
            if (navigation && !isNavigationFailure(navigation, NavigationFailureType.redirected) &&
              !isNavigationFailure(navigation, NavigationFailureType.duplicated)) throw navigation
          } catch (error) {
            this.$message.error(translateUi((error && error.message) || '登录失败，请稍后重试'))
            this.loading = false
            // The server consumes the challenge even when a later login check fails.
            await this.getCaptcha()
            this.$nextTick(() => { if (this.$refs.sliderCaptcha) this.$refs.sliderCaptcha.focusHandle() })
          } finally {
            this.loading = false
          }
        } else {
          // 关闭等待框
          this.loading = false
          this.$message.error(translateUi('请输入所有内容'))
          return false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
/* 品牌色：深蓝 #132D55 / 亮黄 #F4BB1F */
.login-page {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: var(--app-viewport-height, 100dvh);
  box-sizing: border-box;
  padding: 9vh 20px 60px;
  /* 顶部一抹品牌色，柔和渐变到底部浅灰 */
  background:
    radial-gradient(1200px 480px at 50% -120px, rgba(244, 187, 31, .16), transparent 60%),
    radial-gradient(900px 420px at 80% -60px, rgba(19, 45, 85, .10), transparent 65%),
    linear-gradient(180deg, #fafbfc 0%, #f1f3f6 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
}

.login-card {
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
  width: 100%;
  max-width: 446px;
  padding: 34px 32px 30px;
  background: #fff;
  border: 1px solid rgba(19, 45, 85, .06);
  border-radius: 10px;
  box-shadow:
    0 1px 2px rgba(19, 45, 85, .04),
    0 12px 32px -8px rgba(19, 45, 85, .10);
  overflow: hidden;
}

.login-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #f4bb1f 0%, #e89d1a 35%, #132d55 100%);
}

.login-brand-row {
  text-align: center;
  margin-bottom: 6px;
}

.login-logo {
  display: block;
  height: 60px;
  width: auto;
  max-width: 100%;
  margin: 0 auto;
}

.login-welcome {
  margin: 12px 0 2px;
  font-size: 18px;
  line-height: 1.4;
  font-weight: 600;
  color: #132d55;
  text-align: center;
  letter-spacing: .3px;
}

.login-subtitle {
  margin: 0 0 22px;
  font-size: 13px;
  line-height: 1.5;
  color: #7b8794;
  text-align: center;
}

.login-form {
  margin-top: 4px;
}

.login-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  line-height: 1.4;
  color: #1f2d3d;
  cursor: default;
  font-weight: 500;
}

.login-forgot {
  margin: -4px 0 18px;
  font-size: 13px;
  line-height: 1.4;
  text-align: right;

  a {
    color: #1668b3;
    text-decoration: none;
    transition: color .2s;

    &:hover {
      color: #132d55;
      text-decoration: underline;
    }
  }
}

.login-button {
  width: 100%;
  height: 42px;
  border-radius: 4px;
  border-color: #132d55;
  background: #132d55;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: .5px;
  box-shadow: 0 4px 12px -4px rgba(19, 45, 85, .35);
  transition: transform .12s ease, box-shadow .2s ease, background .2s ease;

  &:hover,
  &:focus {
    border-color: #1c4173;
    background: #1c4173;
    box-shadow: 0 6px 16px -4px rgba(19, 45, 85, .45);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.login-welcome-link { margin-left: 15px; color: #7b8794; text-decoration: underline; text-underline-offset: 3px; }

.footer-span {
  position: relative;
  margin-top: 24px;
  flex-shrink: 0;
  width: 100%;
  text-align: center;
  font-size: 12px;
  color: #9aa4b2;
  letter-spacing: .3px;
}

/* Element UI 输入框样式覆盖 */
.login-card ::v-deep .el-form-item {
  margin-bottom: 18px;
}

.login-card ::v-deep .el-input__inner {
  height: 40px;
  line-height: 40px;
  border-radius: 4px;
  border-color: #d8dde5;
  background: #fff;
  font-size: 14px;
  color: #1f2d3d;
  transition: border-color .2s, box-shadow .2s;

  &:hover {
    border-color: #b8c0cc;
  }
}

.login-card ::v-deep .el-input__inner:focus {
  border-color: #f4bb1f;
  box-shadow: 0 0 0 3px rgba(244, 187, 31, .18);
}

.login-card ::v-deep .el-form-item__error {
  padding-top: 3px;
  font-size: 12px;
}

/* 未填写完整时按钮呈浅灰禁用态，与参考样式一致 */
.login-card ::v-deep .el-button.is-disabled,
.login-card ::v-deep .el-button.is-disabled:hover,
.login-card ::v-deep .el-button.is-disabled:focus {
  border-color: #e9eaec;
  background: #e9eaec;
  color: #a9aeb5;
  box-shadow: none;
  transform: none;
}

.login-language-switch {
  position: absolute;
  top: 22px;
  right: 28px;
  z-index: 2;
}

.login-language-button {
  display: inline-flex;
  align-items: center;
  height: 32px;
  border: 1px solid #d8dde5;
  border-radius: 16px;
  padding: 0 12px;
  color: #5a6472;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: border-color .2s, color .2s;

  &:hover {
    border-color: #132d55;
    color: #132d55;
  }
}

.login-language-button i:first-child {
  margin-right: 5px;
  transform: rotate(90deg);
}

.login-language-button i:last-child {
  margin-left: 5px;
  font-size: 10px;
}

.language-dropdown {
  min-width: 118px;
}

.language-dropdown .language-selected {
  color: #132d55;
  background: #fdf6e3;
  font-weight: 600;
}

@media (max-width: 768px) {
  .login-page {
    padding: 6vh 20px 56px;
  }

  .login-card {
    padding: 28px 22px 24px;
  }

  .login-logo {
    height: 50px;
  }
}
</style>
